// ─────────────────────────────────────────────────────────────────────────────
// Dashboard guide page
//
// The first page a user lands on after sign-in is the Dashboard. This page
// walks through every major section of that screen so a new user can match
// what they see to what it means.
//
// Order of sections mirrors the visual order on the actual Dashboard, so a
// user can read top-to-bottom while looking at the page.
// ─────────────────────────────────────────────────────────────────────────────

import {
  GuideLayout, H2First, H2, P, UL, LI, UI,
  ScreenshotFrame, Callout,
} from "../GuideLayout";

export function GuideDashboard() {
  return (
    <GuideLayout
      title="Dashboard"
      subtitle="Your home screen. Today's plan, drawdown room, and a snapshot of what's happening before you place a trade. Available in vertical (default) and horizontal three-column layouts."
      prev={{ slug: "welcome",     label: "Welcome to Futures OS" }}
      next={{ slug: "trade-entry", label: "Trade Entry" }}
    >
      <H2First>What it's for</H2First>
      <P>
        The Dashboard is the screen that opens when you sign in, and it's
        designed to answer one question: <em>"Should I be trading right now,
        and if so, how much room do I have?"</em> Everything on the page is
        organized around that question — your active account, the rules in
        play today, how much drawdown you've used, what's on the calendar.
      </P>
      <P>
        If you only ever check one screen before a session, this is the one.
      </P>

      <ScreenshotFrame
        src="/guide/dashboard-overview.png"
        alt="Dashboard overview"
        caption="The default vertical Dashboard — top to bottom mirrors the order you check things in before a session. A horizontal three-column layout is also available."
      />

      <H2>Vertical vs. horizontal layout</H2>
      <P>
        The Dashboard has two layout modes you can switch between in{" "}
        <UI>Profile Settings → Appearance</UI>:
      </P>
      <UL>
        <LI>
          <strong>Vertical (default)</strong> — sections stack top to
          bottom. Works on any screen size and is the most familiar
          layout. Scroll down to see everything.
        </LI>
        <LI>
          <strong>Horizontal</strong> — three fixed columns side by side,
          everything visible at once without scrolling. Designed for
          wider desktop screens. The welcome message moves to the top
          nav bar, and the columns are resizable by dragging the handle
          between them.
        </LI>
      </UL>
      <P>
        In horizontal mode, the three columns are:
      </P>
      <UL>
        <LI><strong>Left</strong> — Today's Plan (rules), KPIs, Today's Performance</LI>
        <LI><strong>Center</strong> — Account selector, Safe-to-Trade banner, quick actions, Account Equity chart, Prop Firm ROI, Recent Trades</LI>
        <LI><strong>Right</strong> — Economic Calendar, Market Sentiment, Trader Sentiment, News Feed</LI>
      </UL>

      <Callout kind="tip" title="Resizing columns">
        In horizontal mode, hover over the thin divider between any two
        columns — the cursor changes to a resize handle. Drag left or
        right to adjust. Column widths are saved to your browser and
        persist across sessions. Use the <UI>↺ Reset columns</UI> button
        in the top bar (next to the clock) to restore the defaults.
      </Callout>

      <Callout kind="info" title="Horizontal on mobile">
        On a mobile device, horizontal mode adapts automatically: portrait
        orientation stacks all three columns vertically (same content,
        single column); landscape orientation shows the full three-column
        layout with narrower widths. The vertical layout is generally more
        comfortable on phones.
      </Callout>

      <H2>The account selector</H2>
      <P>
        At the very top of the Dashboard is the <UI>Viewing Account</UI>{" "}
        dropdown. Everything below it — rules, drawdown, equity, recent trades —
        reflects whichever account you've selected.
      </P>
      <P>
        If you trade more than one account (a combine plus a funded account,
        say), switch between them here. Each account has its own rules, its
        own drawdown limit, and its own trade history.
      </P>

      <Callout kind="tip">
        Set the account you trade most as your default in <UI>Portfolio</UI>.
        It'll be pre-selected every time you sign in.
      </Callout>

      <H2>Safe to trade banner</H2>
      <P>
        Right under the greeting, a colored banner gives you a quick read on
        whether market conditions favor trading right now. Green means no
        high-impact news in the next 24 hours; yellow means caution windows
        are approaching; red means stay out.
      </P>
      <P>
        This is the same signal you'll see at the top of the <UI>Market</UI>{" "}
        page, just compressed for the home screen. The fuller view with the
        live event timeline lives there.
      </P>

      <H2>Today's snapshot</H2>
      <P>
        A row of four small tiles shows the day's basics at a glance:
      </P>
      <UL>
        <LI><UI>Avg Trade Today</UI> — your average P&L per trade so far today</LI>
        <LI><UI>Trades Today</UI> — count of trades placed today, against your daily max</LI>
        <LI><UI>Win Rate Today</UI> — today's win rate, separate from your all-time number</LI>
        <LI><UI>Discipline</UI> — today's average discipline score across logged trades</LI>
      </UL>
      <P>
        These are zeroed out before your first trade of the day, then update
        as you log. Useful as a real-time check on whether the day is going
        the way you planned.
      </P>

      <H2>Today's Plan</H2>
      <P>
        This is the rule-by-rule status card — the most important section on
        the page if you're working a combine or eval.
      </P>
      <P>
        Each rule you've configured in <UI>Trading Rules</UI> shows up here
        with its current state:
      </P>
      <UL>
        <LI>Green check — you're inside the rule</LI>
        <LI>Red X — you've broken (or are about to break) the rule</LI>
        <LI>Counters where relevant — "0 / 200" means $0 used of your $200 daily drawdown</LI>
      </UL>
      <P>
        The header at the top of the card aggregates everything:{" "}
        <UI>SAFE</UI>, <UI>TAKE CAUTION</UI>, or <UI>STOP TRADING</UI>{" "}
        depending on how many rules are clear vs violated.
      </P>

      <ScreenshotFrame
        src="/guide/dashboard-todays-plan.png"
        alt="Today's Plan card showing rule status"
        caption="Today's Plan: every rule, its current value, and whether you're in or out of bounds."
      />

      <Callout kind="warn">
        Trading Rules in Futures OS are <strong>advisory</strong>, not
        enforced — the app will warn you when you're approaching a limit, but
        it can't stop you from logging a trade that breaks one. Your broker
        and prop firm are still the actual enforcement layer.
      </Callout>

      <H2>Today's Performance</H2>
      <P>
        A focused look at the current day's P&L: total dollars made or lost,
        percentage move, and how much of your daily drawdown buffer remains.
      </P>
      <P>
        Before your first trade, this shows "No trades yet today" with your
        full drawdown buffer available. After trades start logging, it
        updates in real time.
      </P>

      <H2>Account Equity</H2>
      <P>
        A line chart of your account balance over time, with timeframe toggles
        (1W / 1M / 3M / YTD / All) and a switch between <UI>Equity</UI> (raw
        balance) and <UI>Net P&L</UI> (cumulative gain or loss from your
        starting balance).
      </P>
      <P>
        The number above the chart is your current balance; the figure
        beside it ("-$177.22 net all-time") is your cumulative P&L since
        starting the account.
      </P>
      <P>
        Below the chart, a one-line summary shows your overall win/loss
        record and profit factor for the selected timeframe.
      </P>

      <H2>Prop Firm ROI</H2>
      <P>
        If you've logged payouts and expenses in the Portfolio tab, a{" "}
        <UI>💰 Prop Firm ROI</UI> widget appears between the equity
        chart and recent trades. It shows a three-stat summary (total
        payouts, total expenses, net ROI %) and a per-firm breakdown
        with progress bars. Click <UI>Full Tracker →</UI> to go to
        the Portfolio's ROI Tracker tab for the full view. The widget
        is hidden until you have at least one payout or expense logged.
      </P>

      <H2>Recent Trades</H2>
      <P>
        Your last five trades for the active account, with date, symbol,
        setup, direction, result, P&L, and discipline score. Click{" "}
        <UI>View Analytics →</UI> in the header to jump to the full
        Analytics tab; for the full historical list, use the{" "}
        <UI>Journal</UI> page.
      </P>

      <Callout kind="info" title="The half-moon icon">
        If you see a 🌙 between the date and time on a trade row, that
        trade was placed after the daily market close and is grouped
        into the next session day for daily P&L, rule counters, and the
        calendar view. This matches how your broker and prop firm report
        — a trade at 7:49pm ET on Monday counts toward Tuesday's session,
        not Monday's. The moon shows up wherever trades are listed: here,
        in the Journal, and in Trade Review.
      </Callout>

      <H2>Market Sentiment</H2>
      <P>
        On the right side of the Dashboard is a Market Sentiment panel —
        AI-generated daily commentary on broader market conditions, with a
        risk reading (Risk-On / Risk-Off) and a High Impact flag when
        major events are in play.
      </P>
      <Callout kind="info">
        Market Sentiment uses generated commentary as a preview of planned
        AI features. The summary is informational and may contain errors —
        always verify against the source links shown beneath it before
        acting on it.
      </Callout>

      <H2>Trader Sentiment</H2>
      <P>
        Directly below Market Sentiment is your personal <UI>Trader Sentiment</UI>{" "}
        panel. Where Market Sentiment tells you what the market is doing,
        Trader Sentiment tells you how <em>you</em> are trading into it —
        a behavioral read derived entirely from your own data.
      </P>
      <P>
        The score (0–100) is a weighted composite of four signals:
      </P>
      <UL>
        <LI><UI>Win rate</UI> — your percentage of winning trades (40% weight)</LI>
        <LI><UI>Rule adherence</UI> — average discipline score across your logged trades (30% weight)</LI>
        <LI><UI>Drawdown room</UI> — how much of your daily drawdown buffer remains (20% weight)</LI>
        <LI><UI>Avg R:R</UI> — your average risk-to-reward ratio (10% weight)</LI>
      </UL>
      <P>
        The score maps to one of five verdicts:
      </P>
      <UL>
        <LI><strong>Locked In</strong> (75–100) — all signals strong, you're trading at your best</LI>
        <LI><strong>In Rhythm</strong> (58–74) — mostly solid, minor signals worth watching</LI>
        <LI><strong>Steady</strong> (42–57) — neutral, no clear edge in either direction</LI>
        <LI><strong>Drifting</strong> (25–41) — behavioral signals slipping, tighten up</LI>
        <LI><strong>Off Track</strong> (0–24) — multiple signals red, consider stepping back</LI>
      </UL>
      <Callout kind="tip" title="Incomplete data">
        If you haven't configured a daily drawdown rule or haven't scored
        your trades with a discipline rating, those signals are dimmed and
        their weight is redistributed to what's available. A nudge appears
        at the bottom of the panel pointing to what's missing. The more
        data you log, the sharper the score gets.
      </Callout>
      <Callout kind="info">
        Trader Sentiment is a behavioral indicator, not a trade signal. A
        score of "Locked In" doesn't mean go long — it means your habits
        and discipline are strong right now. Use it as context alongside
        your pre-trade checklist, not as a reason to trade.
      </Callout>

      <H2>News Feed</H2>
      <P>
        Live headlines from financial news sources, refreshed periodically.
        Each headline shows its source and how recent it is. Useful as
        ambient context — anything that might move your instruments will
        usually show up here before it shows up on a chart.
      </P>

      <H2>The three action cards</H2>
      <P>
        Between the snapshot tiles and Today's Plan are three quick-action
        cards:
      </P>
      <UL>
        <LI><UI>Log Trade</UI> — jumps to Trade Entry. Used after closing a trade to add it to your history.</LI>
        <LI><UI>Review Last Trade</UI> — opens Trade Review on your most recent trade for a coaching read.</LI>
        <LI><UI>Can I Trade?</UI> — opens the preflight checklist before placing a new order.</LI>
      </UL>
      <P>
        These are shortcuts. Everything they do is reachable from the
        sidebar too — they're just placed on the Dashboard because they're
        the most common things you'll do from the home screen.
      </P>

      <H2>How to use the Dashboard</H2>
      <P>
        A reasonable daily workflow:
      </P>
      <UL>
        <LI><strong>Before your first trade:</strong> check the Safe-to-Trade banner, glance at Today's Plan to confirm you're inside all your rules, check your Trader Sentiment score for a behavioral read, and look at Market Sentiment for broader context.</LI>
        <LI><strong>During the session:</strong> watch the Today's Performance card and the drawdown counter in Today's Plan. If either is approaching your daily limit, the Dashboard is where you'll see it first.</LI>
        <LI><strong>After each trade:</strong> click <UI>Log Trade</UI>, fill in what happened, and the snapshot tiles + equity chart update.</LI>
        <LI><strong>End of session:</strong> use <UI>Review Last Trade</UI> on a notable trade from the day for coaching feedback before you shut down.</LI>
      </UL>
    </GuideLayout>
  );
}
